/**
 * About — authentic story of why The Carp Cast exists. Soft-launch wording:
 * everything that is hidden is described as "coming soon" and no claims are
 * made about partnerships that have not been signed.
 */
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  Fish,
  Users,
  Trophy,
  MapPin,
  Crown,
  Heart,
  ArrowRight,
  Sunrise,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import FisheryImage from '../components/FisheryImage';

const PILLARS = [
  {
    icon: Users,
    title: 'Community First',
    body: 'Real catch reports from real UK anglers. No fluff, no fake influencer culture — just the bank, the rig, the result.',
  },
  {
    icon: Trophy,
    title: 'Genuine Competitions',
    body: 'Monthly competitions, Catch of the Month and a season-long League — scored fairly, judged transparently.',
  },
  {
    icon: MapPin,
    title: 'Lake Intel That Matters',
    body: 'Every UK fishery profiled with facilities, prices, known stock and angler reviews — so you know what to expect before you load the car.',
  },
  {
    icon: Crown,
    title: 'A Premium That Pays Off',
    body: 'Free for everyone. Pro and Elite memberships unlock deeper intel, priority features and exclusive perks as the platform grows.',
  },
];

const ROADMAP = [
  { tag: 'Live now', items: ['Catch logs · Community feed', 'Leagues · Competitions · COTM', 'UK lake database', 'Pro & Elite memberships'] },
  { tag: 'Coming soon', items: ['Direct fishery bookings', 'Premium bait subscription boxes', 'Trusted UK brand partnerships', 'Member-exclusive deals'] },
];

const About = () => {
  return (
    <div className="min-h-screen bg-[#050605]" data-testid="about-page">
      {/* Hero */}
      <div className="relative">
        <FisheryImage
          variant="hero"
          aspect="hero"
          seed="about-hero"
          imgClassName="opacity-60"
          className="md:h-[60vh] min-h-[420px]"
          priority
        >
          <div className="absolute inset-0 flex items-end px-6 md:px-12 pb-12 md:pb-20 z-10">
            <div className="max-w-4xl">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 rounded-full border border-[#c8b273]/30 bg-[#c8b273]/5 px-4 py-1.5 mb-5"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#c8b273]" />
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#c8b273]">
                  About The Carp Cast
                </span>
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-extrabold uppercase tracking-wider text-white leading-[0.95]"
              >
                Built by anglers,<br />for anglers.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="mt-5 max-w-2xl text-base md:text-lg text-[#cfd4cf] leading-relaxed"
              >
                We're modernising the UK carp scene — bringing the community, the
                competitions and the lakes you fish into one place that actually
                feels worth coming back to.
              </motion.p>
            </div>
          </div>
        </FisheryImage>
      </div>

      {/* The vision */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <p className="data-label text-[#c8b273] mb-4">Our story</p>
        <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-extrabold uppercase tracking-wider text-white mb-6">
          Carp Fishing Deserves Better Than Fragmented Forums.
        </h2>
        <div className="space-y-5 text-base md:text-lg text-[#cfd4cf] leading-relaxed">
          <p>
            The Carp Cast started with one frustration: every time we wanted to
            plan a session, the information lived in five different places. Lake
            reports buried in old forum threads. Fish records on personal blogs.
            Competition results on someone's Instagram. Bait advice rotting in
            Facebook groups.
          </p>
          <p>
            We wanted a single home for the UK carp scene — a place where catch
            logs, competitions, lake intel, community chat and (when the time is
            right) trusted brand partnerships all sit alongside each other.
            Premium without being pretentious. Modern without losing the soul of
            what makes carp fishing what it is.
          </p>
          <p>
            We're not a venture-backed marketplace pretending to know the bank.
            We're anglers — the same as you — building the tool we wished
            existed when we started.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-[#070907] py-16 md:py-24 border-y border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="data-label text-[#c8b273] mb-2">What we stand for</p>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-extrabold uppercase tracking-wider text-white">
              The Four Pillars
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="relative rounded-2xl border border-white/10 bg-[#0b0d0b] p-7 md:p-8 hover:border-[#c8b273]/30 transition-colors"
                data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#c8b273]/30 bg-[#c8b273]/10 text-[#c8b273] mb-4">
                  <p.icon className="w-5 h-5" />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white mb-2">
                  {p.title}
                </h3>
                <p className="text-sm md:text-base text-[#a8b0a8] leading-relaxed">
                  {p.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="text-center mb-12">
          <p className="data-label text-[#c8b273] mb-2">The road ahead</p>
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-extrabold uppercase tracking-wider text-white mb-4">
            Where We Are. Where We're Going.
          </h2>
          <p className="text-[#a8b0a8] max-w-2xl mx-auto leading-relaxed">
            Carp Cast is in soft launch. The community, leagues, competitions and
            membership are fully live today. Direct fishery bookings, premium bait
            bundles and trusted brand partnerships will roll out as soon as we
            have the right partners on side.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {ROADMAP.map((col) => (
            <div
              key={col.tag}
              className="rounded-2xl border border-white/10 bg-[#0b0d0b] p-7 md:p-8"
              data-testid={`roadmap-${col.tag.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center gap-2 mb-5">
                <span className="h-1.5 w-1.5 rounded-full bg-[#c8b273] animate-pulse" />
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#c8b273]">{col.tag}</span>
              </div>
              <ul className="space-y-3">
                {col.items.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-[#cfd4cf]">
                    <Fish className="w-4 h-4 text-[#c8b273] mt-1 flex-shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-white/5 bg-[#070907]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(200,178,115,0.10),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <Sunrise className="w-8 h-8 text-[#c8b273] mx-auto mb-4" />
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-extrabold uppercase tracking-wider text-white mb-4">
            Come fish with us.
          </h2>
          <p className="text-base md:text-lg text-[#a8b0a8] max-w-xl mx-auto mb-8 leading-relaxed">
            Free to join. No spam. Just real anglers logging real catches,
            sharing real intel, and competing for real prizes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/register">
              <Button className="btn-gold" data-testid="about-cta-register">
                Create a free account
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="ghost" className="text-[#cfd4cf] hover:text-white hover:bg-white/5" data-testid="about-cta-contact">
                Or get in touch
              </Button>
            </Link>
          </div>
          <p className="mt-6 text-xs text-[#6b7d6b] inline-flex items-center justify-center gap-1.5">
            Made with <Heart className="w-3 h-3 text-[#c8b273] fill-[#c8b273]" /> for UK carp anglers
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
